import { z } from 'zod';

// ---------------------------------------------------------------------------
// Luhn Algorithm for card validation
// ---------------------------------------------------------------------------

/**
 * @param {string} cardNumber - Digits only
 * @returns {boolean}
 */
function luhnCheck(cardNumber) {
    const digits = cardNumber.replace(/\D/g, '');
    let sum = 0;
    let isEven = false;
    for (let i = digits.length - 1; i >= 0; i--) {
        let n = parseInt(digits[i], 10);
        if (isEven) {
            n *= 2;
            if (n > 9) n -= 9;
        }
        sum += n;
        isEven = !isEven;
    }
    return sum % 10 === 0;
}

// ---------------------------------------------------------------------------
// Card Payment Schema
// ---------------------------------------------------------------------------

export const cardFormSchema = z.object({
    cardholderName: z
        .string()
        .min(2, 'Name must be at least 2 characters')
        .max(60, 'Name is too long')
        .regex(/^[a-zA-Z\s'-]+$/, 'Name may only contain letters, spaces, hyphens, and apostrophes'),

    cardNumber: z
        .string()
        .transform((val) => val.replace(/\s/g, ''))
        .refine((val) => /^\d{16}$/.test(val), 'Card number must be 16 digits')
        .refine((val) => luhnCheck(val), 'Invalid card number'),

    expiryDate: z
        .string()
        .regex(/^\d{2}\/\d{2}$/, 'Use MM/YY format')
        .refine((val) => {
            const [mm, yy] = val.split('/').map(Number);
            if (mm < 1 || mm > 12) return false;
            const now = new Date();
            const expiry = new Date(2000 + yy, mm - 1);
            return expiry > now;
        }, 'Card has expired or date is invalid'),

    cvv: z
        .string()
        .regex(/^\d{3,4}$/, 'CVV must be 3 or 4 digits'),

    saveCard: z.boolean().optional().default(false),

    hasBillingAddress: z.boolean().optional().default(false),

    billingAddress: z
        .object({
            line1: z.string().min(3, 'Address is required'),
            line2: z.string().optional(),
            city: z.string().min(2, 'City is required'),
            state: z.string().min(2, 'State is required'),
            zip: z.string().min(4, 'ZIP code is required'),
            country: z.string().min(2, 'Country is required'),
        })
        .optional(),
});

// ---------------------------------------------------------------------------
// UPI Schema
// ---------------------------------------------------------------------------

export const upiFormSchema = z.object({
    upiId: z
        .string()
        .min(3, 'UPI ID is required')
        .regex(/^[\w.\-_]{3,256}@[a-zA-Z]{2,64}$/, 'Enter a valid UPI ID (e.g. name@upi)'),
});

// ---------------------------------------------------------------------------
// Net Banking Schema
// ---------------------------------------------------------------------------

export const netBankingSchema = z.object({
    bankId: z.string().min(1, 'Please select a bank'),
});

// ---------------------------------------------------------------------------
// Wallet Schema
// ---------------------------------------------------------------------------

export const walletSchema = z.object({
    walletId: z.string().min(1, 'Please select a wallet'),
});

// ---------------------------------------------------------------------------
// Coupon Schema
// ---------------------------------------------------------------------------

export const couponSchema = z.object({
    couponCode: z
        .string()
        .min(3, 'Coupon code must be at least 3 characters')
        .max(20, 'Coupon code is too long')
        .regex(/^[A-Z0-9_-]+$/i, 'Invalid coupon code format'),
});
